// Header.tsx
import '../../App.css';
import { useTenant } from '../TenantContext';
import React, { useState, useMemo } from 'react';

interface HeaderProps {
    lbl: string;
}

interface HeaderLinkItem {
    key: string;
    value: string;
}

export default function Header({ lbl }: Readonly<HeaderProps>) {
    const { tenantConfig } = useTenant();
    const backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor;

    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    const imageUrl = tenantConfig?.authority?.authorityBranding?.logoUrl;
    const headerLinksString = tenantConfig?.authority?.authorityBranding?.headerLink;

    // Parse header links with proper error handling
    const headerLinks = useMemo(() => {
        if (!headerLinksString) {
            return [];
        }

        try {
            // Replace literal newlines with spaces to make valid JSON
            // This handles cases where values contain newlines like "Same as Summary Page:\nhttps://..."
            const sanitizedString = headerLinksString.replace(/\r?\n/g, ' ');
            const parsed: HeaderLinkItem[] = JSON.parse(sanitizedString);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error('Failed to parse header links:', error);
            return [];
        }
    }, [headerLinksString]);

    // Extract URL from value string (handles cases like "Text:\nURL" or just "URL")
    const extractUrl = (value: string): string => {
        if (!value) return '';

        // Match http:// or https:// URLs in the string
        const urlMatch = /(https?:\/\/[^\s]+)/.exec(value);
        return urlMatch ? urlMatch[1] : value.trim();
    };








    const handleLogoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const entered = localStorage.getItem('ENTERED_CALCULATOR') === 'true';
        const visitedWelcome = localStorage.getItem('VISITED_WELCOME') === 'true';

        const parts = window.location.pathname.split('/').filter(Boolean);
        const lastTenant = localStorage.getItem('LAST_TENANT');
        const fallbackPath = lastTenant ? `/${lastTenant}` : '/hcrc';
        const tenantPath = parts.length > 0 ? `/${parts[0]}` : fallbackPath;

        if (entered && visitedWelcome) {
            window.location.href = tenantPath;
            return;
        }

        window.location.href = tenantPath;
    };

    return (
        <header
            className="fixed top-0 left-0 w-full z-10 text-white"
            style={{ backgroundColor }}
            role="banner"
        >
            <div className="container mx-auto flex flex-col md:flex-row md:items-center md:justify-between py-4 md:py-0 md:h-[80px] px-4 md:px-[95px]">
                <div className="flex items-center justify-center md:justify-between w-full md:w-auto mb-3 md:mb-0 relative">
                    <div className="text-xl font-bold flex items-center">
                        {imageUrl && (
                            <button
                                onClick={handleLogoClick}
                                className="flex items-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded"
                                aria-label="Home"
                                type="button"
                            >
                                <img
                                    src={imageUrl}
                                    alt={lbl}
                                    className="h-16 w-auto"
                                />
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden absolute right-0 flex items-center text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded p-2"
                        aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        aria-expanded={isMenuOpen}
                        aria-controls="mobile-navigation"
                        type="button"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            {isMenuOpen ? (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            ) : (
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            )}
                        </svg>
                    </button>
                </div>
                <nav
                    id="mobile-navigation"
                    className={`w-full md:w-auto overflow-hidden transition-all duration-300 ease-in-out px-3 ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 md:max-h-full md:opacity-100'}`}
                    aria-hidden={!isMenuOpen}
                    role="navigation"
                    aria-label="Main navigation"
                >
                    <ul className="flex flex-col md:flex-row md:space-x-4 space-y-2 md:space-y-0 items-center md:items-center md:justify-end w-full">
                        {headerLinks.map((link, index) => {
                            const extractedUrl = extractUrl(link.value);
                            return (
                                <li key={`${link.key}-${index}`} className="w-full md:w-auto">
                                    {extractedUrl ? (
                                        <a
                                            href={extractedUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ fontFamily: "'Open Sans', sans-serif" }}
                                            aria-label={`${link.key} (opens in new window)`}
                                            className="block text-center md:inline-block focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 rounded px-2 py-1"
                                        >
                                            {link.key}
                                        </a>
                                    ) : (
                                        <span
                                            style={{
                                                fontFamily: "'Open Sans', sans-serif",
                                                color: 'white',
                                                opacity: 0.7
                                            }}
                                            className="block text-center md:inline-block px-2 py-1"
                                        >
                                            {link.key}
                                        </span>
                                    )}
                                </li>
                            );
                        })}


                    </ul>
                </nav>
            </div>
        </header>
    );
}